import { Fragment } from 'react'
import {
  Text,
  colors,
  Button,
  typography,
  Card,
  Link,
} from '@resiliatech/birdsong'
import styled from '@emotion/styled'
import { Link as RouterLink } from 'react-router-dom'

import {
  useGetChangePlanPortalLinksQuery,
  useGetThirdPartyNonprofitOrgConfigQuery,
  useNonprofitOrgToUserQuery,
  PlatformPlan,
  GetChangePlanPortalLinksQuery,
} from 'Generated/graphql-types'
import { analytics } from 'Modules/Analytics'
import { upgrade } from 'Modules/Analytics/trackActionInterface/Account/upgrade'
import { useAppState, useAppDispatch } from 'Context/AppContext/AppContext'
import useConfigureActivePlans from 'Hooks/useConfigureActivePlans/useConfigureActivePlans'
import { DialogVariant, UPDATE_GLOBAL_DIALOG } from 'TypeDefs/AppContext'
import { plans } from './content'

const Container = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 16px;
`

const Title = styled.div`
  ${typography.up3.semiBoldSans.styles}
`

const BillingLink = styled(RouterLink)`
  text-decoration: none;
  color: ${colors.primary.main};
  font-weight: 600;
`

const Message = styled.div``

const Benefits = styled.p`
  ${typography.up1.regularSans.styles}

  margin: 0 0 16px;

  strong {
    font-weight: 600;
  }
`

const Plans = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
`

const PlanCard = styled(Card)`
  background-color: ${colors.primary.main};
  color: ${colors.illustration.gray100};
  max-width: 414px;
  min-height: 430px;
  height: 100%;
  display: flex;
  flex-flow: column nowrap;
  padding: 16px;
`

const PlanCardTitle = styled.div`
  ${typography.up1.semiBoldSans.styles}
`

const PlanCardPrice = styled.div`
  ${typography.up4.semiBoldSans.styles}

  span {
    ${typography.down1.regularSans.styles}
  }
`

const PlanBullets = styled.ul`
  ${typography.down1.regularSans.styles}
  text-align: left;
  flex-grow: 1;
  padding: 0 0 0 16px;
`

const PlanBullet = styled.li`
  margin-bottom: 4px;
`

const PlanCta = styled(Button)`
  margin: 0 auto;
  background-color: ${colors.accent.lightGreen};
  color: ${colors.primary.main};

  &:hover {
    background-color: #37bf59;
  }
`

const SponsorLink = styled(Link)`
  font-weight: 600;
`

export const UpgradeSell = ({ handleExit }: { handleExit?: () => void }) => {
  const {
    nonprofitOrgConfig: { orgId, role },
    userConfig: { firstName },
  } = useAppState()

  const { isFunderUser } = useConfigureActivePlans()

  const { data: thirdPartyNonprofitOrgConfig } =
    useGetThirdPartyNonprofitOrgConfigQuery({
      variables: { nonprofitOrgId: orgId },
      context: {
        clientName: 'thirdParty',
      },
      skip: isFunderUser || orgId === 0,
    })

  const { data: planData } = useGetChangePlanPortalLinksQuery({
    variables: {
      stripeCustomerId:
        thirdPartyNonprofitOrgConfig?.getThirdPartyNonprofitOrgConfig
          ?.stripeCustomerId || '',
      returnUrl: window.location.href,
    },
    context: {
      clientName: 'thirdParty',
    },
    skip: !thirdPartyNonprofitOrgConfig?.getThirdPartyNonprofitOrgConfig
      ?.stripeCustomerId,
  })

  const { data } = useNonprofitOrgToUserQuery({
    variables: {
      nonprofitOrgId: orgId,
    },
  })

  const handleLinkClick = () => {
    analytics.trackActionCreator(upgrade.Get_Sponsored_Clicked)({
      nonprofitOrgId: orgId,
    })
  }

  const adminEmails = data?.nonprofitOrgToUser?.flatMap((user) => {
    if (user?.user?.email && (user.role === 'ADMIN' || user.role === 'OWNER')) {
      return [user.user.email]
    }

    return []
  })

  const dispatch = useAppDispatch()

  const handleClick = (plan: PlatformPlan) => {
    const selectedPlan = plan.toLowerCase()

    analytics.trackActionCreator(upgrade.Select_Plan_Clicked)({
      nonprofitOrgId: orgId,
      plan,
    })

    const planLink =
      planData?.getChangePlanPortalLinks?.[
        // No great way to type this without hardcoding plan names; trying to avoid that
        selectedPlan as keyof GetChangePlanPortalLinksQuery['getChangePlanPortalLinks']
      ]

    if (planData?.getChangePlanPortalLinks?.isTrialing && planLink) {
      dispatch({
        type: UPDATE_GLOBAL_DIALOG,
        payload: {
          variant: DialogVariant.TRIAL_END,
          isVisible: true,
          url: planLink,
        },
      })
    } else if (planLink) {
      window.location.href = planLink
    }
  }

  const handleContactAdmin = (plan: PlatformPlan) => {
    analytics.trackActionCreator(upgrade.Contact_Admin_Clicked)({
      plan,
    })

    window.open(`mailto:${adminEmails}?subject=Request to Upgrade Your Resilia Account&body=Hi,%0D%0A%0D%0AI would like to upgrade our current Resilia account so we can get access to unlimited features like unlimited Goals %26 Initiatives, unlimited use of Funder Finder, and advanced courses %26 resources created by nonprofit experts.%0D%0A%0D%0A%0D%0AThanks,%0D%0A${firstName}
    `)
  }

  return (
    <Container>
      <Title>Upgrade Now to Unlock Additional&nbsp;Benefits</Title>

      <Message>
        <Benefits>
          With the Resilia{' '}
          {plans.map(({ name }, index) =>
            index === 0 ? (
              <strong key={name}>{name}</strong>
            ) : (
              <Fragment key={name}>
                and <strong>{name}</strong>
              </Fragment>
            )
          )}{' '}
          tier you get access to even more&nbsp;benefits.
        </Benefits>
        <Text variant='p1'>
          To view your current plan’s details,{' '}
          <BillingLink to='/settings/billing' onClick={() => handleExit?.()}>
            visit Manage Billing & Plan
          </BillingLink>
        </Text>
      </Message>
      <Plans>
        {plans.map((plan) => (
          <PlanCard key={plan.plan}>
            <PlanCardTitle>Get {plan.plan}</PlanCardTitle>
            <PlanCardPrice>
              {plan.price}
              <span>/mo</span>
            </PlanCardPrice>
            <PlanBullets>
              {plan.bullets.map((bullet) => (
                <PlanBullet key={bullet}>{bullet}</PlanBullet>
              ))}
            </PlanBullets>
            {(role === 'ADMIN' || role === 'OWNER') && (
              <PlanCta
                variant='contained'
                onClick={() => handleClick(plan.plan)}
              >
                Select Plan
              </PlanCta>
            )}
            {role === 'STAFF' && (
              <PlanCta
                variant='outlined'
                onClick={() => handleContactAdmin(plan.plan)}
              >
                Contact Admin to Upgrade
              </PlanCta>
            )}
          </PlanCard>
        ))}
      </Plans>
      <Text variant='p1'>
        Have a funder?{' '}
        <SponsorLink
          href='http://resilia.com/how-to-get-sponsored'
          target='_blank'
          rel='noopener noreferrer'
          onClick={handleLinkClick}
        >
          Learn how to get sponsored
        </SponsorLink>
      </Text>
    </Container>
  )
}
